/**
 * @file        src/pages/hotel/UpdateBookingModal.jsx
 * @description DB-only admin update for a hotel booking: change the booking
 *              status and add a remark (recorded to the audit trail). Does NOT
 *              call the supplier — a cancellation that must reach TripJack goes
 *              through Cancel. Hotels have no PNR / ticket numbers, so this is
 *              just status + remark (mirrors the flight Update, minus tickets).
 * @author      Kinetika Labs
 * @created     2026-07-16
 * @changelog
 *   - 2026-09-27 Claude — No longer offers Cancellation Requested / Cancelled /
 *                          Refunded: a hand-set "Cancelled" disagreed with TripJack
 *                          and raised no refund. The update route refuses them too.
 *   - 2026-07-16 Claude — Initial (Hotel Booking List Update action).
 *   - 2026-08-10 Claude — Booking Status now uses the themed Select (console-wide
 *                          dropdown consistency pass) instead of a native <select>.
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 *   - 2026-09-15 Claude — Sends booking_status only when the admin changed it. A remark-only update on an On Hold / Hold Expired booking resent that status, which the update route's enum does not accept (400). The current status is also listed in the dropdown so it renders instead of a blank select. Remark capped at 500 chars.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import { updateBooking } from '../../api/hotelBookings';
import { apiErrorMessage } from '../../api/client';

// The statuses this screen may SET (order = the natural lifecycle). The
// cancellation family is deliberately absent — the update route refuses it,
// because a hand-set "Cancelled" never reaches TripJack and raises no refund.
const STATUSES = [
  'Initiated', 'Awaiting confirmation', 'Pending', 'Failed', 'Booked',
];

export default function UpdateBookingModal({ booking, onClose }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(booking.booking_status || 'Booked');
  const [remark, setRemark] = useState('');
  const [err, setErr] = useState('');
  // Statuses this modal can't set (e.g. On Hold) still have to display.
  const statusOptions = (STATUSES.includes(booking.booking_status) || !booking.booking_status
    ? STATUSES : [booking.booking_status, ...STATUSES]).map((s) => ({ value: s, label: s }));

  const mut = useMutation({
    mutationFn: () => updateBooking(booking.id, {
      booking_status: status !== booking.booking_status ? status : undefined,
      remark: remark || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotel-bookings'] });
      qc.invalidateQueries({ queryKey: ['hotel-cancellations'] });
      qc.invalidateQueries({ queryKey: ['hotel-booking-details', booking.id] });
      // The drawer's Timeline card — this update just appended an event to it.
      qc.invalidateQueries({ queryKey: ['booking-logs'] });
      onClose();
    },
    onError: (e) => setErr(apiErrorMessage(e, 'Update failed.')),
  });

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 520 }}>
        <div className="lk-modal-head">
          <h3>Update Booking · #{booking.id}</h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>
        <form className="form-grid" onSubmit={(e) => { e.preventDefault(); setErr(''); mut.mutate(); }}>
          <div className="field">
            <label>Booking Status</label>
            <Select value={status} onChange={setStatus} options={statusOptions} />
          </div>
          <div className="field">
            <label>Remark</label>
            <input value={remark} maxLength={500} onChange={(e) => setRemark(e.target.value)} placeholder="Reason / note (audit trail)" />
          </div>
          <p className="muted full" style={{ fontSize: '.85em' }}>
            This updates the status WE hold — it does not contact the hotel, so it cannot mark a
            stay Cancelled or Refunded. Use <b>Cancel</b> to actually cancel the stay with the
            supplier; a stay cancelled at TripJack's end is recorded automatically.
          </p>
          {err ? <div className="field-error full">{err}</div> : null}
          <div className="form-actions full">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              {mut.isPending ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
